import { DataTable3 } from "@/components/DataTable3/index";
import SharedDataStatus from "@/components/SharedDataStatus/index";
import useDebounce from "@/hooks/use-debounce/index";
import { useNavigate } from '@/lib/router-dom';
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import useDid from "@/hooks/use-did/index";
import useEtr from '@/hooks/use-etr/index';
import useConfig from '@/hooks/use-config/index';
import { featureConstant } from "@/constants/configuration/feature.js";
import { useFeature } from "@/hooks/use-feature/index";
import { renderUserEmailStacked } from '@/utils/formatUserEmail';

export const DataTable = ({
	itemsData,
	pageLimit: _pageLimit,
	currentPage,
	fetchApprovalsData,
	approvalsMeta,
	setCurrentPage,
	loading,
	sortConfig,
	setSortConfig,
}) => {
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const { goTo } = useNavigate();
	const dataTableRef = useRef(null);
	const [pageLimit, setPageLimit] = useState(_pageLimit);
	const [filter, setFilter] = useState({});
	const isInitialRender = useRef(true);
	const debouncedFetchData = useDebounce(() => fetchData({ currentPage }), 500);
	const { toFindOrgName } = useDid()
	const { identity, provideItems } = useConfig()
	const dataElementId = "ebl";

	const { fetchEtr, etrData } = useEtr({
		dataElementId,
		configData: {
			provideItems,
			identity
		}
	})

	useEffect(() => {
		if (provideItems && identity) {
			fetchEtr();
		}
	}, [provideItems, identity]);

	function getOrgName(walletAddress) {
		const orgName = toFindOrgName(walletAddress)
		if (orgName) {
			return orgName
		}
		if (etrData.some(item => item.walletAddress === walletAddress)) {
			return etrData.find((item) => item.walletAddress === walletAddress).name
		}
		return null
	}

	function goToTransaction(messageId, data) {
		if (messageId && data?.transactionType === 'SHARED_DATA') {
			goTo(`/approvals/transaction/${messageId}`, { transactionType: data?.transactionType })
		} else if (messageId && data?.transactionType != 'SHARED_DATA') {
			goTo(`/approvals/transaction/${messageId}/${data?.transferableRecordId}`, { transactionType: data?.transactionType })
		}
		else {
			return
		}
	}

	async function fetchData({ currentPage }) {
		const params = {
			search: filter?.search,
			limit: pageLimit,
			page: currentPage,
		}

		if (sortConfig?.sortBy) {
			params.sortBy = sortConfig?.sortBy;
			params.order = sortConfig.direction ? 'ASC' : 'DESC';
		}

		if (filter?.transactionType) {
			params.transactionType = filter?.transactionType.filter(s => s.$selected).map(s => s.id)
		}
		if (filter?.status) {
			params.status = filter?.status.filter(s => s.$selected).map(s => s.id)
		}
		if (filter?.startDate) {
			params.startDate = filter?.startDate
		}
		if (filter?.endDate) {
			params.endDate = filter?.endDate
		}

		await fetchApprovalsData(params);
	}

	const fetchDataWithPossibleDebounce = () => {
		if (isInitialRender.current) {
			fetchData({ currentPage });
		} else {
			debouncedFetchData();
		}
	};

	useEffect(() => {
		fetchDataWithPossibleDebounce();
		setTimeout(() => {
			isInitialRender.current = false;
		}, 0);
	}, [filter, sortConfig, pageLimit, currentPage]);

	const requestTypes = [
		{
			id: "SHARED_DATA",
			$selected: false,
			label: "Shared Data",
			name: "Shared Data",
		},
		{
			id: "ADD_WALLET",
			$selected: false,
			label: "Add Wallet",
			name: "Add Wallet",
		},
		{
			id: "REMOVE_WALLET",
			$selected: false,
			label: "Remove Wallet",
			name: "Remove Wallet",
		},
		{
			id: "TRANSFER_OWNERSHIP",
			$selected: false,
			label: "Ownership Transfer",
			name: "Ownership Transfer",
		},
		{
			id: "TRANSFER_HOLDERSHIP",
			$selected: false,
			label: "Holdership Transfer",
			name: "Holdership Transfer",
		},
		{
			id: "TRANSFER_OWNERSHIP_HOLDERSHIP",
			$selected: false,
			label: "Ownership Holdership Transfer",
			name: "Ownership Holdership Transfer",
		},
		{
			id: "REJECT_OWNER_TRANSFER",
			$selected: false,
			label: "Ownership Transfer Reject",
			name: "Ownership Transfer Reject",
		},
		{
			id: "REJECT_HOLDER_TRANSFER",
			$selected: false,
			label: "Holdership Transfer Reject",
			name: "Holdership Transfer Reject",
		},
		{
			id: "REJECT_OWNER_HOLDER_TRANSFER",
			$selected: false,
			label: "Ownership Holdership Transfer Reject",
			name: "Ownership Holdership Transfer Reject",
		},
		{
			id: "NOMINATE_OWNERSHIP",
			$selected: false,
			label: "Ownership Nomination",
			name: "Ownership Nomination",
		},
		{
			id: "SURRENDER_RECORD",
			$selected: false,
			label: "Record Surrender",
			name: "Record Surrender",
		}
	];

	const statusTypes = [
		{
			id: "APPROVED",
			$selected: false,
			label: "Approved",
			name: "Approved",
		},
		{
			id: "PENDING_APPROVAL",
			$selected: false,
			label: "Pending Approval",
			name: "Pending Approval",
		},
		{
			id: "REJECTED",
			$selected: false,
			label: "Rejected",
			name: "Rejected",
		},
		{
			id: "REJECTED_BY_SYSTEM",
			$selected: false,
			label: "Rejected by System",
			name: "Rejected by System",
		},
	];

	const filterOptions = [
		{
			key: 'transactionType',
			type: 'multiSelect',
			label: 'Request Type',
			placeHolderText: 'Select',
			options: requestTypes.map(s => ({ ...s, value: s.label.toUpperCase(), name: s.label })),
		},
		{
			key: 'status',
			type: 'multiSelect',
			label: 'Status',
			placeHolderText: 'Select',
			options: statusTypes.map(s => ({ ...s, value: s.label.toUpperCase(), name: s.label })),
		},
		{
			key: 'date',
			type: 'dateRange',
			label: 'Date Created',
			placeHolderText: ['Start Date', 'End Date'],
		}
	]

	const ETRMakerCheckerColumns = [
		{
			id: "elementName",
			header: 'Details',
			accessorKey: 'elementName',
			minSize: 330,
			flex: 0.3,
			sortable: true,
			accessorFn: row => {
				return <>
					<span className="data-table__table__body__content-details__title">
						{row.elementName}
					</span>
					<br />
					{row.transactionType === "SHARED_DATA" &&
						<span className="data-table__table__body__content-details__subtitle">
							{row.recipientOrgName}
						</span>}
					{(row.transactionType === "TRANSFER_OWNERSHIP" || row.transactionType === "REJECT_OWNER_TRANSFER") &&
						<span className="data-table__table__body__content-details__subtitle">
							New Owner: {getOrgName(row?.newOwnerAddress)}
						</span>}
					{(row.transactionType === "TRANSFER_HOLDERSHIP" || row.transactionType === "REJECT_HOLDER_TRANSFER") &&
						<span className="data-table__table__body__content-details__subtitle">
							New Holder: {getOrgName(row?.newHolderAddress)}
						</span>}
					{(row.transactionType === "TRANSFER_OWNERSHIP_HOLDERSHIP" || row.transactionType === "REJECT_OWNER_HOLDER_TRANSFER") &&
						<span className="data-table__table__body__content-details__subtitle">
							New Owner: {getOrgName(row?.newOwnerAddress)}
							<br />
							New Holder: {getOrgName(row?.newHolderAddress)}
						</span>}
					{row.transactionType === "NOMINATE_OWNERSHIP" &&
						<span className="data-table__table__body__content-details__subtitle">
							New Owner: {getOrgName(row?.newOwnerAddress)}
						</span>
					}
				</>
			},
			enableResizing: false,
		},
		{
			id: "status",
			header: 'Status',
			minSize: 250,
			flex: 0.2,
			sortable: true,
			enableResizing: false,
			accessorKey: 'status',
			accessorFn: row => {
				return <SharedDataStatus status={row.status} />
			}
		},
		{
			id: "transactionType",
			header: 'Request Type',
			minSize: 280,
			flex: 0.2,
			sortable: true,
			enableResizing: false,
			accessorKey: "transactionType",
			accessorFn: row => {
				const transactionType = row.transactionType.split("_");
				const renameTransactionType = transactionType.shift();
				transactionType.push(renameTransactionType);
				return <span>
					{row.transactionType === "SHARED_DATA" && "Shared Data"}
					{row.transactionType === "TRANSFER_OWNERSHIP" && "Ownership Transfer"}
					{row.transactionType === "TRANSFER_HOLDERSHIP" && "Holdership Transfer"}
					{row.transactionType === "TRANSFER_OWNERSHIP_HOLDERSHIP" && "Ownership Holdership Transfer"}
					{row.transactionType === "REJECT_OWNER_TRANSFER" && "Ownership Transfer Reject"}
					{row.transactionType === "REJECT_HOLDER_TRANSFER" && "Holdership Transfer Reject"}
					{row.transactionType === "REJECT_OWNER_HOLDER_TRANSFER" && "Ownership Holdership Transfer Reject"}
					{row.transactionType === "NOMINATE_OWNERSHIP" && "Ownership Nomination"}
					{row.transactionType === "SURRENDER_RECORD" && "Record Surrender"}
				</span>
			},
		},
		{
			id: "requestorEmail",
			header: 'Requestor',
			minSize: 250,
			flex: 0.2,
			sortable: true,
			enableResizing: false,
			accessorKey: "requestorEmail",
			cell: ({ row }) => {
				const email = row.original.requestorEmail;
				return isSoftDeleteUserEnabled ? <div className="soft-delete__tooltip-container">{renderUserEmailStacked(email)}{isSoftDeleteUserEnabled && email?.user_status === 'DELETED' && <div className="tooltiptexts eform-tooltip-text">{renderUserEmailStacked(email)}</div>}</div> : <div>{email}</div>;
			},
		},
		{
			id: "modified",
			header: 'Last Modified Date',
			minSize: 200,
			flex: 0.3,
			sortable: true,
			enableResizing: false,
			accessorKey: 'modified',
			accessorFn: row => {
				return <>
					<span className="data-table-last-modified-date">
						{moment(row.modified).format("DD/MM/YYYY")}{" "}
					</span>
					<br />
					<span className="data-table-last-modified-time">
						{moment(row.modified).format("h:mma")}
					</span>
				</>
			},
		}
	];

	return (
		<div className="approval_data_table data-table__content">
			<div className="data-table__content__cols">
				<div className="data-table__content__col">
					<DataTable3
						ref={dataTableRef}
						data={itemsData}
						columns={ETRMakerCheckerColumns}
						isLoading={loading}
						showPageSize={true}

						showInput={false}

						showSelectedActions={false}

						isEditable={false}
						showHeader
						headerProps={{
							filter,
							setFilter,
							searchPlaceholder: "Search Details",
							showSearchInput: true,
							filterOptions,
						}}

						meta={{
							pagination: {
								...approvalsMeta,
								currentPage: approvalsMeta?.page,
								pageSize: pageLimit,
								pageCount: approvalsMeta?.totalPages,
								count: approvalsMeta?.total,
								setPageLimit,
								setCurrentPage,
							}
						}}

						sortConfig={sortConfig}
						setSortConfig={setSortConfig}

						onRowClick={(id, data) => {
							goToTransaction(id, data)
						}}
					/>
				</div>
			</div>
		</div>
	);
};

DataTable.propTypes = {
	itemsData: PropTypes.array.isRequired,
	pageLimit: PropTypes.number.isRequired,
	currentPage: PropTypes.number.isRequired,
	approvalsMeta: PropTypes.object.isRequired,
	setCurrentPage: PropTypes.func,
	fetchApprovalsData: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	sortConfig: PropTypes.object,
	setSortConfig: PropTypes.func,
};
